import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow } from "@/components/shared/Eyebrow";
import { Reveal } from "@/components/shared/Reveal";
import { useServices } from "@/hooks/useContent";

export function Services() {
  const { data: services = [] } = useServices();
  const [active, setActive] = useState(0);

  return (
    <section data-testid="services-section" className="relative bg-dark text-white py-24 md:py-28 overflow-hidden">
      <div className="absolute inset-0 grid-lines-dark opacity-40" />
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <Eyebrow dark className="mb-6">UZMANLIK ALANLARIMIZ</Eyebrow>
            <Reveal>
              <h2 className="font-head font-extrabold tracking-tight text-4xl md:text-5xl lg:text-6xl max-w-2xl leading-[1.02]">
                Endüstriyel ihtiyaçlara uçtan uca mühendislik çözümleri.
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* List */}
          <div className="lg:col-span-7">
            {services.map((s, i) => (
              <Link
                key={s.id || s.slug}
                to={`/hizmetler/${s.slug}`}
                data-testid={`service-row-${s.slug}`}
                onMouseEnter={() => setActive(i)}
                className="group block border-t border-white/10 last:border-b py-7 md:py-8"
              >
                <div className="flex items-center gap-5 md:gap-8">
                  <span className={`font-head text-sm tabular-nums inline-block transition-all duration-300 origin-left ${active === i ? "text-cyan scale-125" : "text-silver/40"}`}>
                    {s.number}
                  </span>
                  <div className="flex-1">
                    <h3 className={`font-head font-bold text-2xl md:text-3xl tracking-tight transition-colors duration-300 ${active === i ? "text-white" : "text-silver/70"}`}>
                      {s.title}
                    </h3>
                    <motion.p
                      className="text-silver/60 text-sm mt-2 max-w-lg overflow-hidden"
                      initial={false}
                      animate={{ height: active === i ? "auto" : 0, opacity: active === i ? 1 : 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      {s.short_desc}
                    </motion.p>
                  </div>
                  <ArrowUpRight className={`w-6 h-6 shrink-0 transition-all duration-300 ${active === i ? "text-cyan translate-x-0" : "text-silver/30 -translate-x-2 opacity-0 group-hover:opacity-100"}`} />
                </div>
              </Link>
            ))}
          </div>

          {/* Image reveal */}
          <div className="lg:col-span-5 hidden lg:block">
            <div className="sticky top-28 aspect-[4/5] relative overflow-hidden bg-navy border border-white/10">
              <AnimatePresence mode="wait">
                {services[active] && (
                  <motion.img
                    key={services[active].id || active}
                    src={services[active].image}
                    alt={services[active].title}
                    className="absolute inset-0 w-full h-full object-cover"
                    initial={{ opacity: 0, scale: 1.08 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  />
                )}
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/20" />
              <div className="absolute top-5 left-5 text-cyan font-head text-4xl font-extrabold">{services[active]?.number}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
